// A job's one page, #/jobs/<id> (BL-128, the job page study's one page): every place that opens a job
// (the board, the lists, the customer record, calls, search, Review's queue, a notification) opens it
// the same way, by its address. Opening it is a step in the browser's history over the place it was
// opened from, which stays as it was under it (App.tsx), so Close or the browser's Back returns there.
// A page reached by a link (a reload, a link someone sent) has nothing behind it in this tab: it is
// drawn over the board, and its Close goes to the place under it, the one its crumb and the rail name.
// The first page's address, #/customers/job/<id>, still opens it. In Blueprint the rows open in place
// (History's in round 2; visits, forms and the signature too in round 3) ride after it,
// `?open=v1,e11,e32`, so a copied link or Back opens the same ones.

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
export const JOB_LINK = new RegExp(`^#/(?:jobs|customers/job)/(${UUID})(?:\\?[^#]*)?$`, 'i');

export const jobHref = (jobId: string) => `#/jobs/${jobId}`;

export function parseJobLink(hash: string): string | null {
  return JOB_LINK.exec(hash)?.[1]?.toLowerCase() ?? null;
}

/** The open rows in a job's address: `#/jobs/<id>?open=v1,e32` → ['v1', 'e32']. */
export function openRowsOf(hash: string): string[] {
  const q = hash.indexOf('?');
  if (q < 0 || !JOB_LINK.test(hash)) return [];
  const open = new URLSearchParams(hash.slice(q + 1)).get('open');
  return open ? [...new Set(open.split(',').filter((x) => /^[\w-]+$/.test(x)))] : [];
}

/** The job's address with these rows open (none: the job's own address). */
export function withOpenRows(hash: string, ids: readonly string[]): string {
  const base = hash.split('?')[0] ?? hash;
  return ids.length ? `${base}?open=${ids.join(',')}` : base;
}

/** What job-page needs of the window: its address and history (a fake one in tests). */
export interface PageWindow {
  location: { hash: string };
  history: { back(): void };
}

/** The job last opened from inside the app, so its Close knows there is a place to go back to. */
let openedFrom: string | null = null;

/** The visit a job's page was opened from (the board's visit panel), so the page opens on it. */
let fromVisit: { job: string; visit: string } | null = null;

/** Opens a job's page: a new history entry over the place it's opened from (a visit, from the board). */
export function openJob(jobId: string, w: PageWindow = window, visitId?: string): void {
  openedFrom = jobId.toLowerCase();
  fromVisit = visitId ? { job: openedFrom, visit: visitId } : null;
  w.location.hash = jobHref(jobId);
}

/** The visit this job's page was opened from, when it was (the record opens Visits on it). */
export const visitOpenedFrom = (jobId: string): string | null =>
  fromVisit?.job === jobId.toLowerCase() ? fromVisit.visit : null;

/** The board's name for it (the board opens a job the same way as everywhere else). */
export const openJobPage = openJob;

/** Whether this job's page was opened from a place in this tab (its crumb names that place). */
export const openedInApp = (jobId: string): boolean => openedFrom === jobId.toLowerCase();

/**
 * Close a job's page: back to the place it was opened from, or, when it came by a link, to the place
 * under it (`under`, the board unless the app says another: the one its crumb and rail name).
 */
export function leaveJobPage(jobId: string, w: PageWindow = window, under = '#/board'): void {
  if (openedFrom === jobId.toLowerCase() && parseJobLink(w.location.hash) === openedFrom) {
    openedFrom = null;
    w.history.back();
    return;
  }
  openedFrom = null;
  w.location.hash = under;
}
